import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { initPush } from '../utils/pushNotifications';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [branding, setBranding] = useState({ platform_name: 'WorksRun', primary_color: '#003366', secondary_color: '#E11D48' });

  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  useEffect(() => {
    axios.get(`${API_URL}/api/branding`).then(res => setBranding(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (token) {
      api.defaults.headers.Authorization = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      // Attempt push subscription silently after login
      initPush(api).catch(() => {});
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await api.post('/auth/register', userData);
    const { access_token, user: newUser } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(newUser);
    return newUser;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (profileData) => {
    const response = await api.put('/users/profile', profileData);
    setUser(response.data);
    return response.data;
  };

  const refreshBranding = () => {
    axios.get(`${API_URL}/api/branding`).then(res => setBranding(res.data)).catch(() => {});
  };

  const value = {
    user,
    token,
    loading,
    branding,
    refreshBranding,
    login,
    register,
    logout,
    updateProfile,
    api,
    isAuthenticated: !!user,
    isJobSeeker: user?.user_type === 'job_seeker',
    isEmployer: user?.user_type === 'employer',
    isCourseProvider: user?.user_type === 'course_provider',
    isAdmin: user?.user_type === 'admin',
    isStudent: user?.user_type === 'student',
    isUniversityManager: user?.user_type === 'university_manager',
    isEventHost: user?.user_type === 'event_host',
    isRecruiter: user?.user_type === 'recruiter',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
